/**
 * ENCOUNTER BIOMES - Smaller combat tiles on the world map
 *
 * Encounters are mini-dungeons with:
 * - No level requirements (anyone can enter)
 * - Smaller hex grid (5x5 instead of 7x7)
 * - Only mobs and healing tiles (no treasure, no traps)
 * - No extra loot drops (just combat rewards)
 * - Shorter cooldowns than full dungeons
 * - Mob difficulty scales with player level
 */

import { ENEMY_TEMPLATES } from '../dungeon/mobs';

// ============================================================================
// ENCOUNTER CONFIG - Shared settings for all encounters
// ============================================================================

export const ENCOUNTER_CONFIG = {
  // Grid size (smaller than dungeons)
  GRID_SIZE: 5,

  // Tile type weights (only combat and rest)
  TILE_WEIGHTS: {
    empty: 15,     // Safe passage
    combat: 55,    // Enemy encounters
    rest: 30,      // Healing campfires
  },

  // No loot drops from encounters (just XP and combat rewards)
  DROP_LOOT: false,

  // Cooldown after completing/exiting (minutes)
  COOLDOWN_MINUTES: 1,

  // Player level scaling for enemy stats
  // Enemies scale to player level within a range
  LEVEL_SCALING: {
    minLevelMult: 0.8,   // Enemies can be 80% of player level
    maxLevelMult: 1.2,   // Enemies can be 120% of player level
  },

  // XP reward for completion: BASE_XP + (playerLevel * LEVEL_MULTIPLIER)
  // Gives ~40 XP at level 1, ~90 XP at level 10, ~190 XP at level 30
  COMPLETION_BASE_XP: 30,
  COMPLETION_LEVEL_MULTIPLIER: 5,
};

// ============================================================================
// ENCOUNTER BIOME DEFINITIONS
// ============================================================================

export const ENCOUNTER_BIOMES = {
  // ─────────────────────────────────────────────────────────────────────────
  // BANDIT CAMP - Common roadside threat
  // ─────────────────────────────────────────────────────────────────────────
  banditCamp: {
    id: 'banditCamp',
    name: 'Bandit Camp',
    icon: '⛺',
    description: 'A crude camp of outlaws. Quick to attack, but not well organized.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#5a4a3a',
    minAge: 1,
    spawnWeight: 12,

    // Encounter-specific properties
    isEncounterBiome: true,
    cooldownMinutes: 1,

    // Mob pool: Mix of low-tier humanoids
    encounterMobPool: ['goblin', 'wolf', 'skeleton'],

    // Entry events
    entryEvents: [
      { id: 'spotted', weight: 40, text: 'Bandits spot you approaching their camp!' },
      { id: 'ambush', weight: 30, text: 'You\'ve stumbled into a bandit ambush!', penalties: { food: 3 } },
      { id: 'abandoned', weight: 20, text: 'The camp looks recently abandoned...', rewards: { gold: 5 } },
      { id: 'supplies', weight: 10, text: 'You find stolen supplies at the camp edge.', rewards: { food: 8, gold: 3 } },
    ],

    // Minimal gathering (this is a hostile tile)
    gatherableResources: {
      gold: { baseAmount: 2, richness: 0.3 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // WOLF DEN - Pack of wild beasts
  // ─────────────────────────────────────────────────────────────────────────
  wolfDen: {
    id: 'wolfDen',
    name: 'Wolf Den',
    icon: '🐺',
    description: 'A cave sheltering a pack of hungry wolves. Dangerous but predictable.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#4a4a4a',
    minAge: 1,
    spawnWeight: 10,

    isEncounterBiome: true,
    cooldownMinutes: 1,

    encounterMobPool: ['wolf', 'cryptSpider'],

    entryEvents: [
      { id: 'growling', weight: 45, text: 'Low growls echo from the den entrance.' },
      { id: 'pack', weight: 30, text: 'The pack is agitated by your presence!' },
      { id: 'sleeping', weight: 15, text: 'The wolves seem to be sleeping...', rewards: { food: 3 } },
      { id: 'pelts', weight: 10, text: 'Old pelts near the entrance.', rewards: { gold: 4 } },
    ],

    gatherableResources: {
      food: { baseAmount: 2, richness: 0.4 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // SPIDER NEST - Creepy crawlies
  // ─────────────────────────────────────────────────────────────────────────
  spiderNest: {
    id: 'spiderNest',
    name: 'Spider Nest',
    icon: '🕷️',
    description: 'Thick webs cover the entrance. Giant spiders lurk within.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#3a3a4a',
    minAge: 1,
    spawnWeight: 8,

    isEncounterBiome: true,
    cooldownMinutes: 1,

    encounterMobPool: ['cryptSpider', 'vaultSlime'],

    entryEvents: [
      { id: 'webs', weight: 40, text: 'Sticky webs cling to everything...' },
      { id: 'cocoons', weight: 25, text: 'Wrapped cocoons hang from the ceiling. Best not to look closely.' },
      { id: 'eggs', weight: 20, text: 'Spider eggs! You should leave before they hatch...', penalties: { food: 2 } },
      { id: 'silk', weight: 15, text: 'Quality spider silk near the entrance.', rewards: { gold: 6 } },
    ],

    gatherableResources: {
      gold: { baseAmount: 2, richness: 0.3 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // GOBLIN WARREN - Underground goblin hideout
  // ─────────────────────────────────────────────────────────────────────────
  goblinWarren: {
    id: 'goblinWarren',
    name: 'Goblin Warren',
    icon: '👺',
    description: 'A network of crude tunnels infested with goblins.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#4a5a3a',
    minAge: 1,
    spawnWeight: 10,

    isEncounterBiome: true,
    cooldownMinutes: 1,

    encounterMobPool: ['goblin', 'cryptRat'],

    entryEvents: [
      { id: 'cackling', weight: 35, text: 'Goblin cackling echoes from within.' },
      { id: 'watchfire', weight: 30, text: 'A small watchfire burns near the entrance.' },
      { id: 'trash', weight: 20, text: 'Goblin trash litters the area.', rewards: { gold: 2 } },
      { id: 'trap', weight: 15, text: 'A crude goblin trap!', penalties: { food: 4 } },
    ],

    gatherableResources: {
      gold: { baseAmount: 3, richness: 0.4 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // SKELETON CRYPT - Undead wandering grounds
  // ─────────────────────────────────────────────────────────────────────────
  skeletonCrypt: {
    id: 'skeletonCrypt',
    name: 'Skeleton Crypt',
    icon: '💀',
    description: 'An old burial ground where the dead walk.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#4a4a5a',
    minAge: 2,
    spawnWeight: 8,

    isEncounterBiome: true,
    cooldownMinutes: 1,

    encounterMobPool: ['skeleton', 'tombGuardian', 'dustyMummy'],

    entryEvents: [
      { id: 'rattling', weight: 40, text: 'Bones rattle in the darkness.' },
      { id: 'grave', weight: 25, text: 'Fresh graves suggest recent activity...', penalties: { food: 2 } },
      { id: 'offerings', weight: 20, text: 'Grave offerings remain undisturbed.', rewards: { gold: 8 } },
      { id: 'quiet', weight: 15, text: 'The dead are at rest... for now.', rewards: { knowledge: 3 } },
    ],

    gatherableResources: {
      knowledge: { baseAmount: 2, richness: 0.4 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // HAUNTED GROVE - Eerie forest spirits
  // ─────────────────────────────────────────────────────────────────────────
  hauntedGrove: {
    id: 'hauntedGrove',
    name: 'Haunted Grove',
    icon: '👻',
    description: 'A twisted part of the forest where spirits linger.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#3a4a3a',
    minAge: 2,
    spawnWeight: 7,

    isEncounterBiome: true,
    cooldownMinutes: 1,

    encounterMobPool: ['wraith', 'drownedZombie'],

    entryEvents: [
      { id: 'whispers', weight: 35, text: 'Ghostly whispers fill the air.' },
      { id: 'cold', weight: 30, text: 'An unnatural chill settles over you.', penalties: { food: 2 } },
      { id: 'lost', weight: 20, text: 'A lost spirit points the way.', rewards: { knowledge: 5 } },
      { id: 'treasure', weight: 15, text: 'Someone buried treasure here long ago.', rewards: { gold: 10 } },
    ],

    gatherableResources: {
      knowledge: { baseAmount: 3, richness: 0.5 },
      magic: { baseAmount: 1, richness: 0.3 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // ORC OUTPOST - Military camp
  // ─────────────────────────────────────────────────────────────────────────
  orcOutpost: {
    id: 'orcOutpost',
    name: 'Orc Outpost',
    icon: '🏕️',
    description: 'A fortified orc position. Well-armed and disciplined.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#5a4a4a',
    minAge: 3,
    spawnWeight: 6,

    isEncounterBiome: true,
    cooldownMinutes: 1,

    encounterMobPool: ['orc', 'darkKnight', 'boneWalker'],

    entryEvents: [
      { id: 'drums', weight: 35, text: 'War drums echo through the camp.' },
      { id: 'patrol', weight: 30, text: 'An orc patrol spots you!' },
      { id: 'supplies', weight: 20, text: 'Unguarded supplies at the edge.', rewards: { food: 10, gold: 5 } },
      { id: 'training', weight: 15, text: 'Orcs are distracted by training.', rewards: { gold: 3 } },
    ],

    gatherableResources: {
      iron: { baseAmount: 2, richness: 0.4 },
      gold: { baseAmount: 2, richness: 0.3 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // DEMON CIRCLE - Summoning site
  // ─────────────────────────────────────────────────────────────────────────
  demonCircle: {
    id: 'demonCircle',
    name: 'Demon Circle',
    icon: '🔥',
    description: 'A sinister ritual site where demons are summoned.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#5a3a3a',
    minAge: 5,
    spawnWeight: 4,

    isEncounterBiome: true,
    cooldownMinutes: 1,

    encounterMobPool: ['demon', 'hellhound', 'cultist'],

    entryEvents: [
      { id: 'flames', weight: 35, text: 'Hellfire burns at the circle\'s center.' },
      { id: 'chanting', weight: 30, text: 'Dark chanting fills the air.', penalties: { food: 5 } },
      { id: 'dormant', weight: 20, text: 'The circle seems dormant...', rewards: { magic: 5 } },
      { id: 'artifacts', weight: 15, text: 'Ritual artifacts lie scattered about.', rewards: { magic: 8, gold: 10 } },
    ],

    gatherableResources: {
      magic: { baseAmount: 4, richness: 0.6 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // SHADOW RIFT - Planar tear
  // ─────────────────────────────────────────────────────────────────────────
  shadowRift: {
    id: 'shadowRift',
    name: 'Shadow Rift',
    icon: '🌑',
    description: 'A tear in reality where shadow creatures emerge.',
    buildingCategory: null,
    associatedBuildings: [],
    color: '#2a2a3a',
    minAge: 7,
    spawnWeight: 3,

    isEncounterBiome: true,
    cooldownMinutes: 1,

    encounterMobPool: ['shadowFiend', 'darkWraith', 'voidStalker'],

    entryEvents: [
      { id: 'void', weight: 35, text: 'The void stares back at you.' },
      { id: 'pull', weight: 30, text: 'You feel the rift pulling at your soul.', penalties: { food: 8 } },
      { id: 'stable', weight: 20, text: 'The rift is stable for now.', rewards: { magic: 10 } },
      { id: 'essence', weight: 15, text: 'Shadow essence crystallizes nearby.', rewards: { magic: 15, artifacts: 2 } },
    ],

    gatherableResources: {
      magic: { baseAmount: 5, richness: 0.7 },
      artifacts: { baseAmount: 1, richness: 0.3 },
    },
    passiveIncome: null,
    canBuildOutpost: false,
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if a biome is an encounter biome
 * @param {string} biomeId - Biome ID to check
 * @returns {boolean} True if encounter biome
 */
export function isEncounterBiome(biomeId) {
  return ENCOUNTER_BIOMES[biomeId]?.isEncounterBiome === true;
}

/**
 * Get encounter info for display
 * @param {string} biomeId - Encounter biome ID
 * @returns {Object|null} Encounter info or null
 */
export function getEncounterInfo(biomeId) {
  const biome = ENCOUNTER_BIOMES[biomeId];
  if (!biome || !biome.isEncounterBiome) return null;

  return {
    id: biome.id,
    name: biome.name,
    icon: biome.icon,
    description: biome.description,
    mobPool: biome.encounterMobPool,
    cooldownMinutes: biome.cooldownMinutes,
  };
}

/**
 * Get mob templates for an encounter
 * @param {string} biomeId - Encounter biome ID
 * @returns {Array} Array of mob template objects
 */
export function getEncounterMobs(biomeId) {
  const biome = ENCOUNTER_BIOMES[biomeId];
  if (!biome || !biome.encounterMobPool) return [];

  return biome.encounterMobPool.map(mobId => ENEMY_TEMPLATES[mobId]).filter(Boolean);
}

/**
 * Calculate enemy level for an encounter based on player level
 * @param {number} playerLevel - Player's current level
 * @returns {number} Enemy level for the encounter
 */
export function calculateEncounterEnemyLevel(playerLevel) {
  const { minLevelMult, maxLevelMult } = ENCOUNTER_CONFIG.LEVEL_SCALING;
  const minLevel = Math.max(1, Math.floor(playerLevel * minLevelMult));
  const maxLevel = Math.ceil(playerLevel * maxLevelMult);

  // Random level within range
  return Math.floor(minLevel + Math.random() * (maxLevel - minLevel + 1));
}

export default ENCOUNTER_BIOMES;
